import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { defectsApi } from "../api/defectsApi";
import { TimesheetEntryModal } from "../../../DailyTimesheetView";
import { ALLOWED_TRANSITIONS } from "../services/defectsViewDerivations";
import EntityDiscussionsTab from "../../common/components/EntityDiscussionsTab";
import DrawerTabs from "../../common/components/DrawerTabs";
import EntityTagsField from "../../common/components/EntityTagsField";
import { createTagMapping } from "../../common/services/tagsApi";
import DueDateChangeModal from "../../common/components/DueDateChangeModal";
import DueDateChangeBadge from "../../common/components/DueDateChangeBadge";
import StatusTransitionReasonModal from "../../common/components/StatusTransitionReasonModal";
import { downloadAttachment } from "../../../shared/utils";

function SearchSelect({ options, value, onChange, placeholder = "Type to search…", hasError }) {
  // options: [{ id, ref_type?, label, sublabel?, description? }]
  // onChange receives the full option object (or null on clear)
  const [query,  setQuery]  = useState("");
  const [open,   setOpen]   = useState(false);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.id === value) || null;

  // Display text: show label when closed and something is selected; show query while typing
  const displayValue = open ? query : (selected ? selected.label + (selected.sublabel ? ` · ${selected.sublabel}` : "") : "");

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(o =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel || "").toLowerCase().includes(q) ||
        (o.description || "").toLowerCase().includes(q) ||
        (o.ref_type || "").toLowerCase().includes(q)
      ).slice(0, 60)
    : options.slice(0, 60);

  const handleFocus = () => {
    setOpen(true);
    setQuery("");
  };

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange(null);  // clear selection when input cleared
  };

  const handleSelect = (opt) => {
    onChange(opt);   // pass full entity object
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className={`dfv2-ss${open ? " open" : ""}${hasError ? " err" : ""}`} ref={wrapRef}>
      <div className="dfv2-ss-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="dfv2-ss-input"
          value={displayValue}
          placeholder={placeholder}
          onFocus={handleFocus}
          onChange={handleInput}
        />
        {selected && !open ? (
          <button className="dfv2-ss-clear" onMouseDown={e => e.preventDefault()} onClick={handleClear} tabIndex={-1}>×</button>
        ) : (
          <svg className="dfv2-ss-chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        )}
      </div>
      {open && (
        <div className="dfv2-ss-drop">
          <div className="dfv2-ss-list">
            {filtered.length === 0 && (
              <div className="dfv2-ss-empty">No matches</div>
            )}
            {filtered.map(opt => (
              <div
                key={`${opt.ref_type || "opt"}-${opt.id}`}
                className={`dfv2-ss-opt${opt.id === value ? " on" : ""}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(opt)}
              >
                {opt.ref_type && <span className="dfv2-ss-type">{opt.ref_type}</span>}
                <div className="dfv2-ss-text">
                  <span className="dfv2-ss-lbl">{opt.label}</span>
                  {opt.sublabel && <span className="dfv2-ss-sub">{opt.sublabel}</span>}
                  {opt.description && <span className="dfv2-ss-desc">{opt.description}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── New Defect Drawer ──────────────────────────────────────────────────────────
const SEVERITY_DUE_DAYS_V2 = { Blocker: 1, Critical: 2, High: 2 };

// Same set DefectsViewV2Page.jsx's Issues-table inline status edit uses —
// these two destination statuses require POST /defects/{id}/transition
// (mandatory reason, reopen_count/defect_reopen_history tracking), not the
// generic PATCH this drawer's own Save button otherwise always uses.
const STATUS_REASON_REQUIRED = new Set(["Rejected", "Re-opened"]);

// Field wrapper — must be defined OUTSIDE the drawer so React doesn't treat it
// as a new component type on every render (which would remount children and
// re-apply autoFocus on the title input, stealing focus from other fields).
function DrwField({ label, req, full, children, err }) {
  return (
    <div className={`dfv2-drw-field${full ? " dfv2-drw-field--full" : ""}`}>
      <label className={`dfv2-drw-label v2drw-form-label${req ? " dfv2-drw-label--req" : ""}`}>{label}</label>
      {children}
      {err && <span className="dfv2-drw-err">{err}</span>}
    </div>
  );
}

function addWorkingDays(days) {
  const d = new Date();
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

function normalizeDefectVisibility(v) {
  const s = String(v || "").trim().toLowerCase();
  if (s === "private")  return "Private";
  if (s === "internal") return "Internal";
  return "Public";
}

const EMPTY_DEFECT_FORM_V2 = {
  ref_type: "project", ref_id: "", project_id: "", customer_id: "", epic_id: "",
  title: "", description: "",
  severity: "", priority: "", status: "Open",
  defect_type: "Functional", environment: "QA",
  build_version: "", release_version: "",
  assigned_to_id: "", reported_by_id: "",
  due_date: "",
  visibility: "Internal",
  source: "", reporting_channel: "",
  steps_to_reproduce: "", expected_result: "", actual_result: "",
  resolution_note: "",
};

function mapDefectToDrawerForm(defect) {
  if (!defect) return { ...EMPTY_DEFECT_FORM_V2, due_date: addWorkingDays(4) };
  return {
    ref_type:           defect.ref_type           || "",
    ref_id:             defect.ref_id             || "",
    project_id:         defect.project_id         || "",
    customer_id:        defect.customer_id        || "",
    epic_id:            defect.epic_id            || "",
    title:              defect.title              || "",
    description:        defect.description        || "",
    severity:           defect.severity           || "",
    priority:           defect.priority           || "",
    status:             defect.status             || "Open",
    defect_type:        defect.defect_type        || "Functional",
    environment:        defect.environment        || "QA",
    build_version:      defect.build_version      || "",
    release_version:    defect.release_version    || "",
    assigned_to_id:     defect.assigned_to_id     || defect.assigned_to || "",
    reported_by_id:     defect.reported_by_id     || defect.reported_by || "",
    due_date:           defect.due_date ? String(defect.due_date).slice(0, 10) : "",
    visibility:         normalizeDefectVisibility(defect.visibility),
    source:             defect.source            || "",
    reporting_channel:  defect.reporting_channel || "",
    steps_to_reproduce: defect.steps_to_reproduce || "",
    expected_result:    defect.expected_result    || "",
    actual_result:      defect.actual_result      || "",
    resolution_note:    defect.resolution_note    || "",
  };
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const ATT_ACCEPT = ".pdf,.xls,.xlsx,.csv,.doc,.docx,.txt,.jpg,.jpeg,.png,.mp4,.mov,.avi,.zip,.rar";
const ATT_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
}

function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  const dd = String(dt.getDate()).padStart(2, "0");
  const mon = MONTHS[dt.getMonth()];
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${dd} ${mon} ${hh}:${mm}`;
}

function parseJsonSafe(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function iconForType(fileType) {
  if (!fileType) return "📎";
  if (fileType.startsWith("image")) return "🖼";
  if (fileType.includes("pdf")) return "📄";
  if (fileType.includes("sheet") || fileType.includes("excel") || fileType.includes("csv")) return "📊";
  if (fileType.includes("word") || fileType.includes("doc")) return "📝";
  return "📎";
}

function DefectDrawer({ mode = "create", defect, initialTab = "overview", user, onClose, onSaved, showToast }) {
  const isEdit = mode === "edit";
  const isExternal = (() => {
    const t = String(user?.db_user_type || "").trim().toLowerCase();
    return t === "external" || t === "external user";
  })();
  const defectId = defect?.id;
  const [form, setForm] = useState(() => (
    isEdit
      ? mapDefectToDrawerForm(defect)
      : {
          ...EMPTY_DEFECT_FORM_V2,
          due_date: addWorkingDays(4),
          reporting_channel: isExternal ? "External QA" : "Internal QA",
        }
  ));
  // Display-only names for the defect's CURRENT project/customer/epic/ref —
  // kept separate from `form` (which is spread wholesale into the save
  // payload, see handleSave) purely so a closed/inactive project/epic/task
  // that's already assigned to this defect can still be shown as the
  // selected value in its picker, even though it's excluded from the
  // active-only options list fetched for NEW selections. Same "inject the
  // current value" pattern already used by the Task drawer's project field
  // (see CLAUDE.md), applied here to Project, Epic, and Task/Feature/
  // Dependency alike. `GET /defects` and `GET /defects/{id}` both already
  // return these names (`_LIST_COLS`/`_LIST_JOINS`, app/routers/defects.py),
  // so no extra fetch is needed.
  const [currentRefMeta, setCurrentRefMeta] = useState(() => (
    isEdit ? {
      project_name:  defect?.project_name  || "",
      customer_name: defect?.customer_name || "",
      epic_name:     defect?.epic_name     || "",
      ref_label:     defect?.ref_label     || "",
    } : {}
  ));
  // Display-only — how many times this defect has been re-opened
  // (defects.reopen_count, incremented server-side by apply_status_transition
  // whenever a transition lands on "Re-opened", regardless of prior status —
  // see app/services/defect_service.py). Not part of `form`/the save payload.
  const [reopenCount, setReopenCount] = useState(() => Number(defect?.reopen_count) || 0);
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [loadingDefect, setLoadingDefect] = useState(isEdit);
  const [drawerTab, setDrawerTab] = useState(isEdit ? (initialTab || "overview") : "overview");
  const [advOpen, setAdvOpen] = useState(false);
  const [users,        setUsers]        = useState([]);
  const [custProjects, setCustProjects] = useState([]);
  const [epicOptions, setEpicOptions] = useState([]);
  const [tasks,        setTasks]        = useState([]);
  const [features,     setFeatures]     = useState([]);
  const [subFeatures,  setSubFeatures]  = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [attachments, setAttachments] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [attDeleting, setAttDeleting] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState("");
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tsEntries, setTsEntries] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [showTsModal, setShowTsModal] = useState(false);
  const [pendingTagIds, setPendingTagIds] = useState([]);
  const [dueDateModal, setDueDateModal] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { fromStatus, toStatus, restPayload }
  const originalDueDate = isEdit ? (defect?.due_date ? String(defect.due_date).slice(0, 10) : "") : null;
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isExternal && drawerTab === "time_entry") {
      setDrawerTab("overview");
    }
  }, [isExternal, drawerTab]);

  // Fetch reference data on mount
  useEffect(() => {
    defectsApi.listUsers()
      .then(data => setUsers(Array.isArray(data) ? data : (data?.users || [])))
      .catch(() => {});
    defectsApi.listCustomersProjects(user?.db_user_id)
      .then(data => setCustProjects(Array.isArray(data) ? data : []))
      .catch(() => {});
    defectsApi.listTasks()
      .then(data => setTasks(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => {});
    defectsApi.listFeatures()
      .then(data => setFeatures(Array.isArray(data) ? data : []))
      .catch(() => {});
    defectsApi.listSubFeatures()
      .then(data => setSubFeatures(Array.isArray(data) ? data : []))
      .catch(() => {});
    defectsApi.listDependencyRequests()
      .then(data => setDependencies(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [user?.db_user_id]);

  useEffect(() => {
    if (!isEdit || !defect?.id) return;
    let cancelled = false;
    setLoadingDefect(true);
    defectsApi.getById(defect.id)
      .then((fullDefect) => {
        if (cancelled) return;
        setForm(mapDefectToDrawerForm(fullDefect));
        setCurrentRefMeta({
          project_name:  fullDefect.project_name  || "",
          customer_name: fullDefect.customer_name || "",
          epic_name:     fullDefect.epic_name     || "",
          ref_label:     fullDefect.ref_label     || "",
        });
        setReopenCount(Number(fullDefect.reopen_count) || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setForm(mapDefectToDrawerForm(defect));
        showToast?.("Loaded summary data only for this defect", "error");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingDefect(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, defect, showToast]);

  useEffect(() => {
    if (!isEdit) return;
    setDrawerTab(initialTab || "overview");
  }, [isEdit, initialTab, defectId]);

  // Auto-switch to Discussions tab on open if the defect already has comments
  useEffect(() => {
    if (!isEdit || !defectId) return;
    let cancelled = false;
    defectsApi.listDiscussions(defectId)
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        if (list.length > 0) {
          setDrawerTab("discussions");
        }
      })
      .catch(() => {});                  // silent — tab stays on overview if fetch fails
    return () => { cancelled = true; };
  }, [isEdit, defectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const projectOptions = useMemo(() => {
    const list = [];
    custProjects.forEach(customer => {
      (customer.projects || []).forEach(project => {
        if (project.project_id) {
          list.push({
            id:          project.project_id,
            label:       project.project_name,
            sublabel:    customer.customer_name,
            customer_id: customer.customer_id,
            project_id:  project.project_id,
          });
        }
      });
    });
    // `custProjects` only ever contains active projects (active_only=true —
    // see defectsApi.listCustomersProjects) — inject the defect's own
    // project as a synthetic entry when it's been closed/deactivated since
    // this defect was filed, so editing an old defect doesn't show a blank
    // Project field. Same pattern as the Task drawer's projectOptions.
    if (isEdit && form.project_id && !list.some((o) => o.id === form.project_id)) {
      list.unshift({
        id:          form.project_id,
        label:       currentRefMeta.project_name || "(inactive project)",
        sublabel:    currentRefMeta.customer_name || "",
        customer_id: form.customer_id,
        project_id:  form.project_id,
      });
    }
    return list;
  }, [custProjects, isEdit, form.project_id, form.customer_id, currentRefMeta.project_name, currentRefMeta.customer_name]);

  // Resolve the currently selected project (for filtering downstream entities)
  const selectedProject = useMemo(() => {
    if (!form.project_id) return null;
    for (const customer of custProjects || []) {
      const p = (customer.projects || []).find(
        (pr) => pr.project_id === form.project_id
      );
      if (p) {
        return {
          project_id:    p.project_id,
          project_name:  p.project_name || "",
          customer_id:   customer.customer_id,
          customer_name: customer.customer_name || "",
        };
      }
    }
    // Not in the active-only list — fall back to the defect's own current
    // project/customer names so relatedEntityOptions/lockedTaskEpic below
    // still resolve correctly for a closed/inactive project (see
    // projectOptions' injection above for why this can happen).
    if (isEdit && currentRefMeta.project_name) {
      return {
        project_id:    form.project_id,
        project_name:  currentRefMeta.project_name,
        customer_id:   form.customer_id,
        customer_name: currentRefMeta.customer_name,
      };
    }
    return null;
  }, [form.project_id, form.customer_id, custProjects, isEdit, currentRefMeta.project_name, currentRefMeta.customer_name]);

  // Build related entity list for the Task / Feature / Dependency field.
  const relatedEntityOptions = useMemo(() => {
    if (!selectedProject) return [];

    const list = [];

    if (isExternal) {
      // External users: tasks are already scoped to their mapped project + Public
      // visibility by the backend — no client-side project re-filter needed.
      // Features and sub-features: show all (no project association on those tables).
      // Dependencies: scope to the selected project by UUID.
      (tasks || []).forEach((t) => {
        if (!t.id) return;
        list.push({
          id:            t.id,
          ref_type:      "task",
          label:         [t.task_code, t.feature, t.sub_feature].filter(Boolean).join(" — ") || t.title || t.task_code || "Untitled Task",
          sublabel:      [t.customer, t.project].filter(Boolean).join(" / "),
          description:   t.description || "",
          customer_name: t.customer || "",
          project_name:  t.project  || "",
          epic_id:       t.epic_id     || "",
          epic_name:     t.epic_name   || "",
          epic_status:   t.epic_status || "",
        });
      });
      (features || []).forEach((f) => {
        if (!f.id) return;
        list.push({
          id:       f.id,
          ref_type: "feature",
          label:    f.feature_name || "Unnamed Feature",
          sublabel: f.product_name || "",
        });
      });
      (subFeatures || []).forEach((sf) => {
        if (!sf.id) return;
        list.push({
          id:       sf.id,
          ref_type: "sub_feature",
          label:    sf.sub_feature_name || "Unnamed Sub-Feature",
          sublabel: sf.sub_feature_code || "",
        });
      });
      (dependencies || []).forEach((d) => {
        if (!d.id) return;
        list.push({
          id:       d.id,
          ref_type: "dependency",
          label:    d.title || "Untitled Dependency",
          sublabel: [d.dependency_type, d.status].filter(Boolean).join(" · "),
        });
      });
    } else {
      // Internal users: tasks scoped to selected project; features, sub-features,
      // and dependencies shown in full (no project filter on those).
      const norm = (s) => String(s || "").trim().toLowerCase();
      const projectName  = norm(selectedProject.project_name);
      const customerName = norm(selectedProject.customer_name);

      (tasks || []).filter((t) => {
        const tp = norm(t.project);
        const tc = norm(t.customer);
        return tp === projectName && (!tc || tc === customerName);
      }).forEach((t) => {
        if (!t.id) return;
        list.push({
          id:            t.id,
          ref_type:      "task",
          label:         [t.feature, t.sub_feature].filter(Boolean).join(" — ") || t.title || t.task_code || "Untitled Task",
          sublabel:      [t.customer, t.project].filter(Boolean).join(" / "),
          description:   t.description || "",
          customer_name: t.customer || "",
          project_name:  t.project  || "",
          epic_id:       t.epic_id     || "",
          epic_name:     t.epic_name   || "",
          epic_status:   t.epic_status || "",
        });
      });
      (features || []).forEach((f) => {
        if (!f.id) return;
        list.push({
          id:       f.id,
          ref_type: "feature",
          label:    f.feature_name || "Unnamed Feature",
          sublabel: f.product_name || "",
        });
      });
      (subFeatures || []).forEach((sf) => {
        if (!sf.id) return;
        list.push({
          id:       sf.id,
          ref_type: "sub_feature",
          label:    sf.sub_feature_name || "Unnamed Sub-Feature",
          sublabel: sf.sub_feature_code || "",
        });
      });
      (dependencies || []).forEach((d) => {
        if (!d.id) return;
        list.push({
          id:       d.id,
          ref_type: "dependency",
          label:    d.title || "Untitled Dependency",
          sublabel: [d.dependency_type, d.status].filter(Boolean).join(" · "),
        });
      });
    }

    // Keep the currently-selected ref visible even if it doesn't fit the new
    // project filter (e.g. opening a defect saved under a different project).
    if (form.ref_id && !list.some((o) => o.id === form.ref_id)) {
      const fromTask = (tasks || []).find((t) => t.id === form.ref_id);
      if (fromTask) {
        list.unshift({
          id:           fromTask.id,
          ref_type:     "task",
          label:        [fromTask.task_code, fromTask.feature, fromTask.sub_feature].filter(Boolean).join(" — ") || fromTask.title || fromTask.task_code || "Untitled Task",
          sublabel:     [fromTask.customer, fromTask.project].filter(Boolean).join(" / "),
          description:  fromTask.description || "",
          epic_id:      fromTask.epic_id     || "",
          epic_name:    fromTask.epic_name   || "",
          epic_status:  fromTask.epic_status || "",
        });
      } else {
        const fromFeature = (features || []).find((f) => f.id === form.ref_id);
        if (fromFeature) {
          list.unshift({
            id:       fromFeature.id,
            ref_type: "feature",
            label:    fromFeature.feature_name || "Unnamed Feature",
            sublabel: fromFeature.product_name || "",
          });
        } else {
          const fromSubFeature = (subFeatures || []).find((sf) => sf.id === form.ref_id);
          if (fromSubFeature) {
            list.unshift({
              id:       fromSubFeature.id,
              ref_type: "sub_feature",
              label:    fromSubFeature.sub_feature_name || "Unnamed Sub-Feature",
              sublabel: fromSubFeature.sub_feature_code || "",
            });
          } else {
            const fromDep = (dependencies || []).find((d) => d.id === form.ref_id);
            if (fromDep) {
              list.unshift({
                id:       fromDep.id,
                ref_type: "dependency",
                label:    fromDep.title || "Untitled Dependency",
                sublabel: [fromDep.dependency_type, fromDep.status].filter(Boolean).join(" · "),
              });
            } else if (isEdit && currentRefMeta.ref_label) {
              // Last-resort fallback: the ref wasn't found in any of the
              // fetched task/feature/sub_feature/dependency lists at all
              // (e.g. soft-deleted, or excluded by a filter none of the
              // above account for) — use the name the defect record itself
              // already carries so the field isn't blank.
              list.unshift({
                id:       form.ref_id,
                ref_type: form.ref_type,
                label:    currentRefMeta.ref_label,
                sublabel: "",
              });
            }
          }
        }
      }
    }
    return list;
  }, [isExternal, selectedProject, tasks, features, subFeatures, dependencies, form.ref_id, form.ref_type, isEdit, currentRefMeta.ref_label]);

  // If this defect is filed against a task that already has its own epic,
  // that epic is ALWAYS the defect's epic — attached automatically and
  // locked (can't be changed here), even if it's Closed. Derived from
  // relatedEntityOptions so it stays in sync with whatever ref_id/ref_type
  // is currently selected, including on initial edit-mode load.
  const lockedTaskEpic = useMemo(() => {
    if (form.ref_type !== "task" || !form.ref_id) return null;
    const opt = relatedEntityOptions.find(o => o.id === form.ref_id && o.ref_type === "task");
    return (opt && opt.epic_id) ? { id: opt.epic_id, name: opt.epic_name, status: opt.epic_status } : null;
  }, [form.ref_type, form.ref_id, relatedEntityOptions]);

  useEffect(() => {
    if (lockedTaskEpic && form.epic_id !== lockedTaskEpic.id) {
      set("epic_id", lockedTaskEpic.id);
    }
  }, [lockedTaskEpic]); // eslint-disable-line react-hooks/exhaustive-deps

  // Epic options scoped to the selected project — active-only when
  // creating, but include Closed epics while EDITING (same
  // `include_closed=${isEdit}` convention the Task drawer already uses) so
  // a defect whose epic was closed after filing still shows it selected
  // instead of a blank field. The onChange guard below still blocks
  // assigning a DIFFERENT Closed epic — re-selecting the defect's own is a
  // no-op. Skipped entirely when the epic is auto-attached from a linked
  // task (lockedTaskEpic) — that field is locked, not a dropdown.
  useEffect(() => {
    if (!form.project_id || lockedTaskEpic) {
      setEpicOptions([]);
      return;
    }
    let cancelled = false;
    defectsApi.listEpics(form.project_id, { includeClosed: isEdit })
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setEpicOptions(list.map(ep => ({
          id: ep.id,
          label: ep.epic_name,
          status: ep.status,
        })));
      })
      .catch(() => { if (!cancelled) setEpicOptions([]); });
    return () => { cancelled = true; };
  }, [form.project_id, lockedTaskEpic, isEdit]);

  const assignableUsers = useMemo(
    () => (users || []).map(u => ({
      id: u.id,
      label: u.display_name || u.name || u.user_name || "Unnamed User",
      sublabel: u.user_name || "",
      description: u.role ? `${u.role}${u.user_type ? ` · ${u.user_type}` : ""}` : (u.user_type || ""),
    })),
    [users]
  );

  const reportedByDisplay = useMemo(() => {
    const reported = (users || []).find((u) => u.id === form.reported_by_id);
    if (reported) return reported.display_name || reported.name || reported.user_name || "";
    return user?.name || user?.email || user?.preferred_username || "";
  }, [users, form.reported_by_id, user]);

  // Auto-populate Reported By from logged-in user
  useEffect(() => {
    if (isEdit) return;
    if (!user || !users.length) return;
    const email = user.preferred_username || user.upn || user.email || "";
    if (!email) return;
    const match = users.find(u => u.user_name?.toLowerCase() === email.toLowerCase());
    if (match) setForm(f => ({ ...f, reported_by_id: match.id }));
  }, [isEdit, user, users]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const loadAttachments = useCallback(async () => {
    if (!defectId) return;
    setAttLoading(true);
    try {
      const data = await defectsApi.listAttachments(defectId);
      setAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      setAttachments([]);
      showToast?.(err.message || "Failed to load attachments", "error");
    } finally {
      setAttLoading(false);
    }
  }, [defectId, showToast]);

  const loadHistory = useCallback(async () => {
    if (!defectId) return;
    setHistoryLoading(true);
    try {
      const [statusHistory, auditLogs] = await Promise.all([
        defectsApi.listStatusHistory(defectId),
        defectsApi.listAuditLogs(defectId).catch(() => []),
      ]);

      const statusEvents = (Array.isArray(statusHistory) ? statusHistory : []).map((h) => ({
        ...h,
        event_kind: "status",
      }));
      const attachmentEvents = (Array.isArray(auditLogs) ? auditLogs : [])
        .filter(
          (entry) =>
            entry?.column_key === "attachment_upload" ||
            entry?.column_key === "attachment_delete"
        )
        .map((entry) => {
          const payload = parseJsonSafe(entry.new_value) || parseJsonSafe(entry.old_value) || {};
          const isDelete = entry.column_key === "attachment_delete";
          return {
            id: `attachment-${entry.id}`,
            event_kind: "attachment",
            changed_by_name: entry.changed_by || "System",
            changed_at: entry.changed_at,
            reason: payload.file_name || "Attachment",
            attachment_action: isDelete ? "Deleted attachment" : "Uploaded attachment",
          };
        });

      const merged = [...statusEvents, ...attachmentEvents].sort((a, b) => {
        const at = new Date(a.changed_at || 0).getTime();
        const bt = new Date(b.changed_at || 0).getTime();
        return at - bt;
      });
      setHistory(merged);
    } catch (err) {
      setHistory([]);
      showToast?.(err.message || "Failed to load history", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [defectId, showToast]);

  const loadTimeEntries = useCallback(async () => {
    if (!defectId) return;
    setTsLoading(true);
    try {
      const data = await defectsApi.listTimeEntries(defectId);
      setTsEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      setTsEntries([]);
      showToast?.(err.message || "Failed to load time entries", "error");
    } finally {
      setTsLoading(false);
    }
  }, [defectId, showToast]);

  useEffect(() => {
    if (!isEdit || !defectId) return;
    if (drawerTab === "attachments" && attachments === null) loadAttachments();
    if (drawerTab === "history" && history === null) loadHistory();
    if (drawerTab === "time_entry" && tsEntries === null) loadTimeEntries();
  }, [
    isEdit, defectId, drawerTab, attachments, history, tsEntries,
    loadAttachments, loadHistory, loadTimeEntries,
  ]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSeverityChange = (sev) => {
    const dueDays = SEVERITY_DUE_DAYS_V2[sev] ?? 4;
    setForm(f => ({ ...f, severity: sev, due_date: addWorkingDays(dueDays) }));
  };

  const validate = () => {
    const e = {};
    if (!form.project_id)    e.project_id = "Required";
    if (!isExternal && !form.epic_id) e.epic_id = "Required";
    if (!form.ref_id)        e.ref_id = "Required";
    if (!form.title.trim())  e.title  = "Required";
    if (!form.severity)      e.severity = "Required";
    if (!form.priority)      e.priority = "Required";
    if (!form.source)        e.source = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (isEdit) {
      const originalAssigneeId = defect?.assigned_to_id || defect?.assigned_to || "";
      const nextAssigneeId = form.assigned_to_id || "";
      const assigneeChanged = !!nextAssigneeId && nextAssigneeId !== originalAssigneeId;
      const statusUnchanged = (form.status || "") === (defect?.status || "");
      if (assigneeChanged && statusUnchanged) {
        const assignee = (users || []).find((u) => u.id === nextAssigneeId);
        const assigneeName =
          assignee?.display_name || assignee?.name || assignee?.user_name || "selected user";
        const confirmed = window.confirm(
          `Are you sure to assign to ${assigneeName} without changing the status?`
        );
        if (!confirmed) return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      payload.assigned_to = payload.assigned_to_id || undefined;
      payload.reported_by = payload.reported_by_id || undefined;
      delete payload.assigned_to_id;
      delete payload.reported_by_id;
      // External users can only create Public defects
      payload.visibility = isExternal ? "Public" : normalizeDefectVisibility(payload.visibility);

      if (!payload.assigned_to && user) {
        const email = user.preferred_username || user.upn || user.email || "";
        const match = users.find(u => u.user_name?.toLowerCase() === email.toLowerCase());
        if (match) payload.assigned_to = match.id;
      }

      if (payload.project_id && !payload.customer_id) {
        for (const customer of custProjects || []) {
          const matchedProject = (customer.projects || []).find((p) => p.project_id === payload.project_id);
          if (matchedProject) {
            payload.customer_id = customer.customer_id;
            break;
          }
        }
      }

      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.reported_by) delete payload.reported_by;
      if (!payload.customer_id) delete payload.customer_id;
      if (!payload.project_id)  delete payload.project_id;
      const userIdSet = new Set((users || []).map(u => u.id));
      if (payload.assigned_to && !userIdSet.has(payload.assigned_to)) delete payload.assigned_to;
      if (payload.reported_by && !userIdSet.has(payload.reported_by)) delete payload.reported_by;

      if (!payload.due_date) {
        if (!isEdit && payload.severity) {
          const dueDays = SEVERITY_DUE_DAYS_V2[payload.severity] ?? 4;
          payload.due_date = addWorkingDays(dueDays);
        } else {
          delete payload.due_date;
        }
      }

      if (isEdit) {
        const newDueDate = payload.due_date ? String(payload.due_date).slice(0, 10) : "";
        const dueDateChanged = !!newDueDate && newDueDate !== originalDueDate;
        // A status change into Rejected/Re-opened must go through
        // POST /transition (mandatory reason, reopen_count/
        // defect_reopen_history tracking) — never the generic PATCH below,
        // which has no transition validation, no reason requirement, and
        // never touches reopen tracking at all. Checked before the
        // due-date interrupt so both can chain (status reason first, then
        // due-date reason) rather than one silently dropping the other.
        const statusChangedToReasonRequired =
          form.status !== (defect?.status || "") && STATUS_REASON_REQUIRED.has(form.status);
        if (statusChangedToReasonRequired) {
          setSaving(false);
          setPendingStatusChange({
            fromStatus: defect?.status || "",
            toStatus: form.status,
            restPayload: payload,
            dueDateChanged,
            newDueDate,
          });
          return;
        }
        if (dueDateChanged) {
          setSaving(false);
          setDueDateModal({ fromDate: originalDueDate, toDate: newDueDate, pendingPayload: payload });
          return;
        }
        await defectsApi.update(defect.id, payload);
      } else {
        const created = await defectsApi.create(payload);
        const createdId = created?.id;
        if (createdId && pendingTagIds.length > 0) {
          await Promise.allSettled(
            pendingTagIds.map(tagId =>
              createTagMapping({ module_name: "defects", entity_id: createdId, tag_id: tagId })
            )
          );
        }
        setPendingTagIds([]);
      }
      showToast?.(isEdit ? "Defect updated" : "Defect created", "success");
      onSaved();
    } catch (err) {
      showToast?.(err.message || (isEdit ? "Failed to update defect" : "Failed to create defect"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Shared tail for both "resume after a reason modal" paths below. When
  // `statusTransition` is set, applies it via POST /transition FIRST (the
  // only call that validates the transition, requires a reason, and
  // updates reopen_count/defect_reopen_history), then PATCHes whatever else
  // changed with `status` excluded — including it would make update_defect
  // write a redundant no-op "X -> X" defect_status_history row on top of
  // the one the transition call already wrote correctly.
  const commitEdit = async (payload, statusTransition) => {
    setSaving(true);
    try {
      if (statusTransition) {
        await defectsApi.transitionStatus(defect.id, statusTransition.toStatus, statusTransition.reason);
      }
      const patchPayload = { ...payload };
      if (statusTransition) delete patchPayload.status;
      await defectsApi.update(defect.id, patchPayload);
      showToast?.("Defect updated", "success");
      onSaved();
    } catch (err) {
      showToast?.(err.message || "Failed to update defect", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusReasonConfirm = async (reason) => {
    const { toStatus, restPayload, dueDateChanged, newDueDate } = pendingStatusChange;
    setPendingStatusChange(null);
    // A due-date change bundled into the same save still needs its own
    // mandatory reason — chain into that modal rather than silently
    // dropping it or saving it without a reason.
    if (dueDateChanged) {
      setDueDateModal({
        fromDate: originalDueDate,
        toDate: newDueDate,
        pendingPayload: restPayload,
        pendingStatusTransition: { toStatus, reason },
      });
      return;
    }
    await commitEdit(restPayload, { toStatus, reason });
  };

  const handleDueDateConfirm = async (reason) => {
    const { pendingPayload, pendingStatusTransition } = dueDateModal;
    setDueDateModal(null);
    await commitEdit({ ...pendingPayload, due_date_change_reason: reason }, pendingStatusTransition || null);
  };

  const resetFileSelection = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFileError("");
    if (!f) {
      setSelectedFile(null);
      return;
    }
    if (f.size > ATT_MAX_FILE_SIZE_BYTES) {
      setFileError("File exceeds the 2 MB limit.");
      setSelectedFile(null);
      e.target.value = "";
      return;
    }
    setSelectedFile(f);
  };

  const handleUploadAttachment = async () => {
    if (!selectedFile || !defectId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("ref_type", "defect");
      fd.append("ref_id", String(defectId));
      fd.append("caption", "Defects");
      await defectsApi.uploadAttachment(fd);
      resetFileSelection();
      await loadAttachments();
      showToast?.("Attachment uploaded", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to upload attachment", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      await downloadAttachment(att.id);
    } catch (err) {
      showToast?.(err.message || "Failed to download attachment", "error");
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!att?.id) return;
    const ok = window.confirm(`Delete ${att.file_name || "this attachment"}?`);
    if (!ok) return;
    setAttDeleting(att.id);
    try {
      await defectsApi.deleteAttachment(att.id);
      setAttachments(prev => (prev || []).filter(a => a.id !== att.id));
    } catch (err) {
      showToast?.(err.message || "Failed to delete attachment", "warn");
    } finally {
      setAttDeleting(null);
    }
  };

  return (
    <>
      <div className="dfv2-drw-scrim v2drw-scrim" />
      <div className="dfv2-drw v2drw" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="dfv2-drw-head v2drw-head">
          <div className="v2drw-head-main">
            <div className="dfv2-drw-eyebrow v2drw-eyebrow">Defects</div>
            <div className="dfv2-drw-title v2drw-title">{isEdit ? (form.title || "Edit Defect") : "New Defect"}</div>
          </div>
          <button className="dfv2-drw-close v2drw-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {isEdit && (
          <DrawerTabs
            tabs={[
              { key: "overview", label: "Overview", icon: "overview" },
              { key: "discussions", label: "Discussions", icon: "discussions" },
              { key: "attachments", label: "Attachments", icon: "attachments" },
              ...(isExternal ? [] : [{ key: "time_entry", label: "Time Entry", icon: "time" }]),
              { key: "history", label: "History", icon: "history" },
            ]}
            activeTab={drawerTab}
            onChange={setDrawerTab}
          />
        )}

        {/* Body */}
        <div className="dfv2-drw-body v2drw-body">
          {isEdit && loadingDefect && (
            <div className="dfv2-drw-load">Loading latest defect data…</div>
          )}
          {(!isEdit || drawerTab === "overview") && (
          <>
          <div className="dfv2-drw-grid">

            <DrwField label="Project" req full err={errors.project_id}>
              <SearchSelect
                options={projectOptions}
                value={form.project_id}
                placeholder="Search project…"
                hasError={!!errors.project_id}
                onChange={project => {
                  if (!project) {
                    // Clear ref too — the previously-selected task/feature/dep
                    // belongs to whatever project we just cleared.
                    setForm(f => ({ ...f, project_id: "", customer_id: "", ref_id: "", ref_type: "", epic_id: "" }));
                    if (errors.project_id) setErrors(er => ({ ...er, project_id: "" }));
                    return;
                  }
                  setForm(f => {
                    const next = {
                      ...f,
                      project_id: project.project_id || project.id,
                      customer_id: project.customer_id || "",
                    };
                    // Reset ref_id/epic_id when the project actually changes so
                    // a stale selection from a different project can't carry over.
                    if (f.project_id && f.project_id !== next.project_id) {
                      next.ref_id = "";
                      next.ref_type = "";
                      next.epic_id = "";
                    }
                    return next;
                  });
                  if (errors.project_id) setErrors(er => ({ ...er, project_id: "" }));
                }}
              />
            </DrwField>

            <DrwField label="Epic" req={!isExternal} full err={errors.epic_id}>
              {lockedTaskEpic ? (
                <>
                  <input
                    type="text"
                    className="dfv2-drw-input v2drw-control"
                    value={lockedTaskEpic.name + (lockedTaskEpic.status === "Closed" ? " (Closed)" : "")}
                    disabled
                    readOnly
                  />
                  <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                    Inherited from the linked task's epic — cannot be changed here.
                  </div>
                </>
              ) : (
                <SearchSelect
                  options={epicOptions}
                  value={form.epic_id}
                  placeholder="Search epic…"
                  hasError={!!errors.epic_id}
                  onChange={epic => {
                    if (!epic) {
                      set("epic_id", "");
                      if (errors.epic_id) setErrors(er => ({ ...er, epic_id: "" }));
                      return;
                    }
                    // Re-selecting the defect's own Closed epic is a no-op;
                    // assigning a DIFFERENT Closed epic is blocked — same
                    // convention as the Task drawer's epic guard.
                    if (epic.status === "Closed" && epic.id !== form.epic_id) {
                      showToast?.("Cannot assign a Closed epic to a defect", "error");
                      return;
                    }
                    set("epic_id", epic.id);
                    if (errors.epic_id) setErrors(er => ({ ...er, epic_id: "" }));
                  }}
                />
              )}
            </DrwField>

            <DrwField label="Task / Feature / Dependency" req full err={errors.ref_id}>
              <SearchSelect
                options={relatedEntityOptions}
                value={form.ref_id}
                placeholder="Search task / feature / dependency…"
                hasError={!!errors.ref_id}
                onChange={entity => {
                  if (!entity) {
                    setForm(f => ({ ...f, ref_id: "", ref_type: "" }));
                    if (errors.ref_id) setErrors(er => ({ ...er, ref_id: "" }));
                    return;
                  }
                  // Do NOT reset epic_id here — feature/sub_feature/
                  // dependency have no epic relation at all, and even for
                  // task-to-task changes there's no need to clear it first:
                  // the lockedTaskEpic effect below already overwrites
                  // epic_id whenever the newly-selected task has its own
                  // epic. Any manually-picked (or previously auto-locked)
                  // epic is left exactly as-is otherwise — switching the
                  // ref entity alone is never a reason to lose it.
                  setForm(f => ({ ...f, ref_id: entity.id, ref_type: entity.ref_type }));
                  if (errors.ref_id) setErrors(er => ({ ...er, ref_id: "" }));
                }}
              />
            </DrwField>

            <DrwField label="Title" req full err={errors.title}>
              <input
                type="text"
                className={`dfv2-drw-input v2drw-control${errors.title ? " err" : ""}`}
                value={form.title}
                onChange={e => { set("title", e.target.value); if (errors.title) setErrors(er => ({ ...er, title: "" })); }}
                placeholder="Brief description of the defect"
                autoFocus
              />
            </DrwField>

            <DrwField label="Description" full>
              <textarea
                className="dfv2-drw-textarea v2drw-control"
                value={form.description}
                onChange={e => set("description", e.target.value)}
                placeholder="Describe the defect…"
              />
            </DrwField>

            {isEdit && (
              <DrwField label="Resolution" full>
                <textarea
                  className="dfv2-drw-textarea v2drw-control"
                  value={form.resolution_note}
                  onChange={e => set("resolution_note", e.target.value)}
                  placeholder="Developers can enter detailed resolution steps here…"
                />
              </DrwField>
            )}

            <DrwField label="Severity" req err={errors.severity}>
              <select
                className={`dfv2-drw-select v2drw-control${errors.severity ? " err" : ""}`}
                value={form.severity}
                onChange={e => {
                  handleSeverityChange(e.target.value);
                  if (errors.severity) setErrors(er => ({ ...er, severity: "" }));
                }}
              >
                <option value="">Select severity</option>
                {["Blocker","Critical","High","Medium","Low"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </DrwField>

            <DrwField label="Priority" req err={errors.priority}>
              <select
                className={`dfv2-drw-select v2drw-control${errors.priority ? " err" : ""}`}
                value={form.priority}
                onChange={e => {
                  set("priority", e.target.value);
                  if (errors.priority) setErrors(er => ({ ...er, priority: "" }));
                }}
              >
                <option value="">Select priority</option>
                {["Critical","High","Medium","Low"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </DrwField>

            {isEdit && (
              <DrwField label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  Status
                  {reopenCount > 0 && (
                    <span className="dfv2-reopen-badge" title={`Re-opened ${reopenCount} time${reopenCount === 1 ? "" : "s"}`}>
                      ↺ {reopenCount}
                    </span>
                  )}
                </span>
              }>
                <select className="dfv2-drw-select v2drw-control" value={form.status} onChange={e => set("status", e.target.value)}>
                  <option value={defect?.status || form.status}>{defect?.status || form.status}</option>
                  {(ALLOWED_TRANSITIONS[defect?.status] || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </DrwField>
            )}

            <DrwField label="Visibility">
              <select
                className="dfv2-drw-select v2drw-control"
                value={normalizeDefectVisibility(form.visibility)}
                onChange={e => set("visibility", normalizeDefectVisibility(e.target.value))}
                disabled={isExternal}
              >
                {isExternal ? (
                  <option value="Public">Public</option>
                ) : (
                  <>
                    <option value="Public">Public</option>
                    <option value="Internal">Internal</option>
                    <option value="Private">Private</option>
                  </>
                )}
              </select>
            </DrwField>

            <DrwField label="Source" req err={errors.source}>
              <select
                className={`dfv2-drw-select v2drw-control${errors.source ? " err" : ""}`}
                value={form.source}
                onChange={e => {
                  set("source", e.target.value);
                  if (errors.source) setErrors(er => ({ ...er, source: "" }));
                }}
              >
                <option value="">Select source</option>
                {["Web","Mobile","API","Infra","Others"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </DrwField>

            <DrwField label="Reporting Channel">
              <select className="dfv2-drw-select v2drw-control" value={form.reporting_channel} onChange={e => set("reporting_channel", e.target.value)}>
                <option value="">Select reporting channel</option>
                {["Email","Call","Client Portal","WhatsApp","Telegram","Internal QA","External QA","Monitoring","Meeting"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </DrwField>

            <DrwField label="Environment">
              <select className="dfv2-drw-select v2drw-control" value={form.environment} onChange={e => set("environment", e.target.value)}>
                {["Dev","QA","UAT","Staging","Production"].map(env => <option key={env} value={env}>{env}</option>)}
              </select>
            </DrwField>

            <DrwField label={<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Due Date {isEdit && defectId && <DueDateChangeBadge entityType="defect" entityId={defectId} />}</span>}>
              <input type="date" className="dfv2-drw-input v2drw-control" value={form.due_date} onChange={e => set("due_date", e.target.value)} />
            </DrwField>

            <DrwField label="Assigned To">
              <SearchSelect
                options={assignableUsers}
                value={form.assigned_to_id}
                onChange={(opt) => set("assigned_to_id", opt?.id || "")}
                placeholder="Search assignee…"
              />
            </DrwField>

          </div>

          {/* Tags */}
          <EntityTagsField
            moduleName="defects"
            moduleScope="defects"
            entityId={isEdit ? defectId : undefined}
            title="Tags"
            draftTagIds={pendingTagIds}
            onDraftTagIdsChange={!isEdit ? setPendingTagIds : undefined}
          />

          {/* Advanced */}
          <div className="dfv2-drw-adv">
            <button type="button" className="dfv2-drw-adv-toggle" onClick={() => setAdvOpen(o => !o)}>
              <svg className={`dfv2-drw-adv-chev${advOpen ? " open" : ""}`} viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              Advanced
            </button>
            {advOpen && (
              <div className="dfv2-drw-grid" style={{ marginTop: 14 }}>

                <DrwField label="Reported By">
                  {isEdit ? (
                    <select className="dfv2-drw-select v2drw-control" value={form.reported_by_id} onChange={e => set("reported_by_id", e.target.value)}>
                      <option value="">— Select —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.name}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="dfv2-drw-input v2drw-control"
                      value={reportedByDisplay}
                      readOnly
                    />
                  )}
                </DrwField>

                <DrwField label="Defect Type">
                  <select className="dfv2-drw-select v2drw-control" value={form.defect_type} onChange={e => set("defect_type", e.target.value)}>
                    {["Functional","UI","Performance","Security","Integration","Data","Other"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </DrwField>

                <DrwField label="Build Version">
                  <input type="text" className="dfv2-drw-input v2drw-control" value={form.build_version} onChange={e => set("build_version", e.target.value)} placeholder="e.g. 1.2.3" />
                </DrwField>

                <DrwField label="Release Version">
                  <input type="text" className="dfv2-drw-input v2drw-control" value={form.release_version} onChange={e => set("release_version", e.target.value)} placeholder="e.g. v2.0" />
                </DrwField>

                <DrwField label="Steps to Reproduce" full>
                  <textarea className="dfv2-drw-textarea v2drw-control" value={form.steps_to_reproduce} onChange={e => set("steps_to_reproduce", e.target.value)} placeholder={"1. Go to…\n2. Click…"} />
                </DrwField>

                <DrwField label="Expected Result">
                  <textarea className="dfv2-drw-textarea v2drw-control" value={form.expected_result} onChange={e => set("expected_result", e.target.value)} placeholder="What should happen" />
                </DrwField>

                <DrwField label="Actual Result">
                  <textarea className="dfv2-drw-textarea v2drw-control" value={form.actual_result} onChange={e => set("actual_result", e.target.value)} placeholder="What actually happens" />
                </DrwField>

              </div>
            )}
          </div>
          </>
          )}

          {isEdit && drawerTab === "discussions" && (
            <EntityDiscussionsTab entityType="defect" entityId={defectId} showToast={showToast} />
          )}

          {isEdit && drawerTab === "attachments" && (
            <>
              <div className="dfv2-att-toolbar">
                <div className="dfv2-att-upload">
                  <input ref={fileInputRef} type="file" accept={ATT_ACCEPT} className="dfv2-att-file-input" onChange={handleFileChange} />
                  <button className="dfv2-att-add-btn" type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    {selectedFile ? "Change File" : "Choose File"}
                  </button>
                  <span className="dfv2-att-file-name">{selectedFile ? selectedFile.name : "No file selected"}</span>
                  <button className="dfv2-att-upload-btn" type="button" onClick={handleUploadAttachment} disabled={uploading || !selectedFile || !!fileError}>
                    {uploading ? "Uploading…" : "Upload"}
                  </button>
                  {selectedFile && (
                    <button className="dfv2-att-clear-btn" type="button" onClick={resetFileSelection} disabled={uploading}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {fileError && <div className="dfv2-att-file-error">{fileError}</div>}
              <div className="dfv2-att-list">
                {attLoading ? (
                  <div className="dfv2-att-empty">Loading…</div>
                ) : !attachments || attachments.length === 0 ? (
                  <div className="dfv2-att-empty">No attachments yet.</div>
                ) : (
                  attachments.map((a) => (
                    <div key={a.id} className="dfv2-att-card">
                      <span className="dfv2-att-icon">{iconForType(a.file_type)}</span>
                      <div className="dfv2-att-info">
                        <button
                          type="button"
                          onClick={() => handleDownloadAttachment(a)}
                          className="dfv2-att-name"
                          title={a.file_name}
                          style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer" }}
                        >
                          {a.file_name}
                        </button>
                        <div className="dfv2-att-meta">
                          {a.file_type && <span className="dfv2-att-tag">{a.file_type.split("/").pop()}</span>}
                          {a.file_size_kb > 0 && (
                            <span className="dfv2-att-tag dfv2-att-tag--size">
                              {a.file_size_kb < 1024 ? `${a.file_size_kb} KB` : `${(a.file_size_kb / 1024).toFixed(1)} MB`}
                            </span>
                          )}
                          {a.caption && <span className="dfv2-att-tag dfv2-att-tag--cat">{a.caption}</span>}
                          {a.uploader_name && <span className="dfv2-att-by">by {a.uploader_name}</span>}
                          {a.created_at && (
                            <span className="dfv2-att-date">
                              {new Date(a.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                      <button className="dfv2-att-del" type="button" onClick={() => handleDeleteAttachment(a)} disabled={attDeleting === a.id} title="Delete attachment">
                        {attDeleting === a.id ? "…" : "✕"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {isEdit && drawerTab === "history" && (
            <div className="dfv2-hst-wrap">
              {historyLoading ? (
                <div className="dfv2-hst-empty">Loading history…</div>
              ) : !history || history.length === 0 ? (
                <div className="dfv2-hst-empty">No history entries yet.</div>
              ) : (
                <div className="dfv2-hst-timeline">
                  {history.map((h) => (
                    <div key={h.id} className="dfv2-hst-item">
                      <div className="dfv2-hst-dot" />
                      <div className="dfv2-hst-content">
                        {h.event_kind === "attachment" ? (
                          <>
                            <div className="dfv2-hst-row">
                              <strong>{h.attachment_action}</strong>
                              <span className="dfv2-hst-by">by {h.changed_by_name || "—"}</span>
                              <span className="dfv2-hst-when">{fmtDateTime(h.changed_at)}</span>
                            </div>
                            {h.reason && <div className="dfv2-hst-reason">{h.reason}</div>}
                          </>
                        ) : (
                          <>
                            <div className="dfv2-hst-row">
                              <strong>{h.from_status || "—"}</strong>
                              <span className="dfv2-hst-arrow">→</span>
                              <strong>{h.to_status || "—"}</strong>
                              <span className="dfv2-hst-by">by {h.changed_by_name || "—"}</span>
                              <span className="dfv2-hst-when">{fmtDateTime(h.changed_at)}</span>
                            </div>
                            {h.reason && <div className="dfv2-hst-reason">{h.reason}</div>}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {isEdit && drawerTab === "time_entry" && (
            <div className="dfv2-ts-section">
              <div className="dfv2-tab-actions">
                <button className="dfv2-att-add-btn" type="button" onClick={() => setShowTsModal(true)}>+ Log Time</button>
              </div>
              {tsLoading && <div className="dfv2-ts-empty">Loading…</div>}
              {!tsLoading && tsEntries !== null && tsEntries.length === 0 && (
                <div className="dfv2-ts-empty">No time entries yet.</div>
              )}
              {!tsLoading && tsEntries && tsEntries.length > 0 && (
                <table className="dfv2-ts-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Time</th>
                      <th>Logged By</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tsEntries.map((e) => (
                      <tr key={e.id}>
                        <td>{fmtDate(`${e.report_date}T00:00:00Z`)}</td>
                        <td>{e.title || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{(e.hours_spent || 0) > 0 || (e.minutes_spent || 0) > 0 ? `${e.hours_spent || 0}h ${e.minutes_spent || 0}m` : "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{e.user_display_name || e.user_name || "—"}</td>
                        <td>{e.status || "Draft"}</td>
                        <td>{e.overall_notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dfv2-drw-foot v2drw-foot">
          <span className="dfv2-drw-foot-spacer v2drw-foot-spacer" />
          <button className="dfv2-drw-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          {(!isEdit || drawerTab === "overview") && (
            <button className="dfv2-drw-btn-save" onClick={handleSave} disabled={saving || (isEdit && loadingDefect)}>
              {saving ? "Saving…" : (isEdit ? "Update defect" : "Create defect")}
            </button>
          )}
        </div>

      </div>
      {showTsModal && defectId && createPortal(
        <TimesheetEntryModal
          refType="defect"
          refId={defectId}
          showToast={showToast}
          onClose={() => setShowTsModal(false)}
          onCreated={loadTimeEntries}
        />,
        document.body
      )}

      {dueDateModal && createPortal(
        <DueDateChangeModal
          fromDate={dueDateModal.fromDate}
          toDate={dueDateModal.toDate}
          onConfirm={handleDueDateConfirm}
          onCancel={() => setDueDateModal(null)}
        />,
        document.body
      )}

      {pendingStatusChange && createPortal(
        <StatusTransitionReasonModal
          fromStatus={pendingStatusChange.fromStatus}
          toStatus={pendingStatusChange.toStatus}
          onConfirm={handleStatusReasonConfirm}
          onCancel={() => setPendingStatusChange(null)}
        />,
        document.body
      )}
    </>
  );
}


export default DefectDrawer;
